// import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import "@/index.css";
import App from "./App.tsx";
import { scheduleAllowSignalR } from "@/lib/signalRGate";

document.title = import.meta.env.VITE_APP_TITLE || "Lefarma app";

// Detección de red celular/ahorro de datos (lite)
const isCellularOrSaveData = () => {
  type NetInfo =
    | { effectiveType?: string; type?: string; saveData?: boolean }
    | undefined;
  const anyNav = navigator as unknown as {
    connection?: NetInfo;
    mozConnection?: NetInfo;
    webkitConnection?: NetInfo;
  };
  const conn =
    anyNav.connection || anyNav.mozConnection || anyNav.webkitConnection;
  const effectiveType = conn?.effectiveType || "";
  const isSlow = /(slow-2g|2g|3g)/i.test(effectiveType);
  const isCell = /cellular/i.test(conn?.type || "");
  const saveData = Boolean(conn?.saveData);
  return isSlow || isCell || saveData;
};

// Evitar precargar AG Grid donde no se usa.
const shouldLoadAgGrid = () => {
  // Red celular / ahorro de datos: nunca precargar
  if (isCellularOrSaveData()) return false;

  const p = location.pathname;
  // home/login no muestran tablas; las grids registran AG Grid on-demand (gate agReady).
  if (/\/(home|login)(\/|$)/.test(p)) return false;
  // Rutas lite conocidas
  if (/(^\/sr$)|(\/agenda\/(prospecto-busqueda|prospecto-nuevo)$)/.test(p))
    return false;
  return true;
};

// Configurar AG Grid en diferido para reducir el TTI inicial (solo si corresponde)
const deferAgGrid = async () => {
  try {
    // Cargar todo bajo demanda para no incluir nada en el bundle inicial
    const { ModuleRegistry } = await import("ag-grid-community");
    if (import.meta.env.VITE_IS_ENTERPRISE === "true") {
      const {
        AllEnterpriseModule,
        RowGroupingModule,
        PivotModule,
        TreeDataModule,
      } = await import("ag-grid-enterprise");
      ModuleRegistry.registerModules([
        AllEnterpriseModule,
        RowGroupingModule,
        PivotModule,
        TreeDataModule,
      ]);
    } else {
      const { AllCommunityModule } = await import("ag-grid-community");
      ModuleRegistry.registerModules([AllCommunityModule]);
    }
  } catch (e) {
    console.warn("AG Grid deferred load failed", e);
  }
};

// Intentar después del primer render/idle
// Intentar después del primer render/idle
type WindowWithRIC = Window & {
  requestIdleCallback?: (
    callback: IdleRequestCallback,
    options?: IdleRequestOptions
  ) => number;
};
const w = window as WindowWithRIC;
if (shouldLoadAgGrid()) {
  if (typeof w.requestIdleCallback === "function") {
    w.requestIdleCallback(() => deferAgGrid());
  } else {
    setTimeout(() => deferAgGrid(), 0);
  }
}

// Registrar Service Worker para optimización de caché
if ("serviceWorker" in navigator && import.meta.env.PROD) {
  window.addEventListener("load", async () => {
    try {
      const registration = await navigator.serviceWorker.register("/sw.js", {
        scope: "/",
      });

      // Verificar por actualizaciones del SW cada 24 horas
      setInterval(async () => {
        await registration.update();
      }, 24 * 60 * 60 * 1000);

      // Cleanup de cache periódico
      if (registration.active) {
        registration.active.postMessage({ type: "CLEANUP_CACHE" });
      }
    } catch (error) {
      console.warn("Error al registrar Service Worker:", error);
    }
  });
}

// En desarrollo usar StrictMode, en producción no para evitar dobles conexiones SignalR
const AppComponent = import.meta.env.DEV ? (
  <App />
) : (
  // <StrictMode>
  // </StrictMode>
  <App />
);
const rootEl = document.getElementById("root")!;
// Asegurar que removemos cualquier HTML de carga previo (skeleton/login lite)
rootEl.innerHTML = "";
createRoot(rootEl).render(AppComponent);

// Señal para index.html: la app ya montó
declare global {
  interface Window {
    __appMounted?: boolean;
  }
}
window.__appMounted = true;

// Defer SignalR allowance until after load+idle, much longer on mobile (final final)
scheduleAllowSignalR();
